import type { ITagProps, ITagSize } from "./types";
import type { MouseEvent } from "react";
import { useCallback } from "react";
import "./index.css";
import { X } from "lucide-react";
import { clsx } from "clsx";
import type { ITypographyVariant } from "../Typography/types";
import { Typography } from "../Typography";

export const Tag = ({
  color = "layer1",
  size = "sm",
  shape = "rounded",
  text,
  startIcon,
  onRemove,
  onClick,
  disabled,
  className,
  ...htmlProps
}: ITagProps) => {
  const rootClass: string = clsx(
    "nuco-tag",
    `nuco-tag--${color}`,
    `nuco-tag--${size}`,
    `nuco-tag--${shape}`,
    !disabled && onClick && "nuco-tag--clickable",
    className,
  );

  const typographyMap: Record<ITagSize, ITypographyVariant> = {
    sm: "subtitle",
    md: "body",
    lg: "body",
    xl: "body",
  };

  const handleClick = useCallback(
    (e: MouseEvent): void => {
      e.stopPropagation();
      if (disabled) {
        return;
      }
      if (onClick) {
        onClick(e);
      }
    },
    [onClick, disabled],
  );

  const handleRemove = useCallback(
    (e: MouseEvent): void => {
      e.stopPropagation();
      if (disabled) {
        return;
      }
      if (onRemove) {
        onRemove(e);
      }
    },
    [onRemove, disabled],
  );

  return (
    <div className={rootClass} onClick={handleClick} {...htmlProps}>
      {startIcon && <div className="nuco-tag__icon">{startIcon}</div>}
      <Typography
        as="div"
        className="nuco-tag__text"
        variant={typographyMap[size]}
        text={text}
      />
      {!disabled && onRemove && (
        <button className="nuco-tag__remove" onClick={handleRemove}>
          <X size="1rem" />
        </button>
      )}
    </div>
  );
};
